import {
  PollyEngine,
  PollyLanguage,
  POLLY_LANGUAGE_LABELS,
} from '@shared/interfaces/polly';

export const engines = Object.values(PollyEngine);

export const DEFAULT_SCRIPT_LANGUAGES: PollyLanguage[] = [
  PollyLanguage.ENGLISH,
  PollyLanguage.FRENCH,
  PollyLanguage.PORTUGUESE,
  PollyLanguage.SPANISH,
];

export const languageLabels: Record<PollyLanguage, string> = {
  ...POLLY_LANGUAGE_LABELS,
  [PollyLanguage.FRENCH]: 'Français',
  [PollyLanguage.GERMAN]: 'Deutsch',
  [PollyLanguage.ITALIAN]: 'Italiano',
  [PollyLanguage.JAPANESE]: '日本語',
  [PollyLanguage.PORTUGUESE]: 'Português',
  [PollyLanguage.SPANISH]: 'Español',
};

export const languageFlags: Record<PollyLanguage, string> = {
  [PollyLanguage.ARABIC]: '🇸🇦',
  [PollyLanguage.CHINESE]: '🇨🇳',
  [PollyLanguage.CZECH]: '🇨🇿',
  [PollyLanguage.DANISH]: '🇩🇰',
  [PollyLanguage.DUTCH]: '🇳🇱',
  [PollyLanguage.ENGLISH]: '🇺🇸',
  [PollyLanguage.FINNISH]: '🇫🇮',
  [PollyLanguage.FRENCH]: '🇫🇷',
  [PollyLanguage.GERMAN]: '🇩🇪',
  [PollyLanguage.HINDI]: '🇮🇳',
  [PollyLanguage.ICELANDIC]: '🇮🇸',
  [PollyLanguage.ITALIAN]: '🇮🇹',
  [PollyLanguage.JAPANESE]: '🇯🇵',
  [PollyLanguage.KOREAN]: '🇰🇷',
  [PollyLanguage.NORWEGIAN]: '🇳🇴',
  [PollyLanguage.POLISH]: '🇵🇱',
  [PollyLanguage.PORTUGUESE]: '🇧🇷',
  [PollyLanguage.ROMANIAN]: '🇷🇴',
  [PollyLanguage.RUSSIAN]: '🇷🇺',
  [PollyLanguage.SPANISH]: '🇪🇸',
  [PollyLanguage.SWEDISH]: '🇸🇪',
  [PollyLanguage.TURKISH]: '🇹🇷',
  [PollyLanguage.WELSH]: '🏴󠁧󠁢󠁷󠁬󠁳󠁿',
};

type VoicesByGender = { female: string[]; male: string[] };

export const voicesByLanguage: Record<
  PollyLanguage,
  Partial<Record<PollyEngine, VoicesByGender>>
> = {
  [PollyLanguage.ARABIC]: {
    [PollyEngine.NEURAL]: { female: ['Hala'], male: ['Zayd'] },
    [PollyEngine.STANDARD]: { female: ['Zeina'], male: [] },
  },
  [PollyLanguage.CHINESE]: {
    [PollyEngine.NEURAL]: { female: ['Zhiyu'], male: [] },
    [PollyEngine.STANDARD]: { female: ['Zhiyu'], male: [] },
  },
  [PollyLanguage.CZECH]: {
    [PollyEngine.NEURAL]: { female: ['Jitka'], male: [] },
  },
  [PollyLanguage.DANISH]: {
    [PollyEngine.NEURAL]: { female: ['Sofie'], male: [] },
    [PollyEngine.STANDARD]: { female: ['Naja'], male: ['Mads'] },
  },
  [PollyLanguage.DUTCH]: {
    [PollyEngine.NEURAL]: { female: ['Laura'], male: [] },
    [PollyEngine.STANDARD]: { female: ['Lotte'], male: ['Ruben'] },
  },
  [PollyLanguage.ENGLISH]: {
    [PollyEngine.NEURAL]: {
      female: ['Amy', 'Danielle', 'Emma', 'Joanna', 'Niamh', 'Olivia', 'Ruth'],
      male: ['Brian', 'Gregory', 'Kevin', 'Matthew', 'Stephen'],
    },
    [PollyEngine.STANDARD]: {
      female: [
        'Aditi',
        'Amy',
        'Emma',
        'Ivy',
        'Joanna',
        'Kendra',
        'Kimberly',
        'Nicole',
        'Raveena',
        'Salli',
      ],
      male: [
        'Brian',
        'Geraint',
        'Joey',
        'Justin',
        'Matthew',
        'Patrick',
        'Russell',
      ],
    },
    [PollyEngine.LONG_FORM]: {
      female: ['Danielle', 'Ruth'],
      male: ['Gregory'],
    },
    [PollyEngine.GENERATIVE]: {
      female: ['Amy', 'Danielle', 'Joanna', 'Niamh', 'Ruth', 'Salli'],
      male: ['Matthew', 'Stephen'],
    },
  },
  [PollyLanguage.FINNISH]: {
    [PollyEngine.NEURAL]: { female: ['Suvi'], male: [] },
    [PollyEngine.STANDARD]: { female: ['Suvi'], male: [] },
  },
  [PollyLanguage.FRENCH]: {
    [PollyEngine.NEURAL]: { female: ['Lea'], male: ['Remi'] },
    [PollyEngine.STANDARD]: { female: ['Celine', 'Lea'], male: ['Mathieu'] },
  },
  [PollyLanguage.GERMAN]: {
    [PollyEngine.NEURAL]: { female: ['Vicki'], male: ['Daniel'] },
    [PollyEngine.STANDARD]: { female: ['Marlene', 'Vicki'], male: ['Hans'] },
  },
  [PollyLanguage.HINDI]: {
    [PollyEngine.NEURAL]: { female: ['Kajal'], male: [] },
    [PollyEngine.STANDARD]: { female: ['Aditi'], male: [] },
  },
  [PollyLanguage.ICELANDIC]: {
    [PollyEngine.STANDARD]: { female: ['Dora'], male: ['Karl'] },
  },
  [PollyLanguage.ITALIAN]: {
    [PollyEngine.NEURAL]: { female: ['Bianca'], male: ['Adriano'] },
    [PollyEngine.STANDARD]: { female: ['Bianca', 'Carla'], male: ['Giorgio'] },
  },
  [PollyLanguage.JAPANESE]: {
    [PollyEngine.NEURAL]: { female: ['Kazuha', 'Tomoko'], male: ['Takumi'] },
    [PollyEngine.STANDARD]: { female: ['Mizuki'], male: ['Takumi'] },
  },
  [PollyLanguage.KOREAN]: {
    [PollyEngine.NEURAL]: { female: ['Seoyeon'], male: [] },
    [PollyEngine.STANDARD]: { female: ['Seoyeon'], male: [] },
  },
  [PollyLanguage.NORWEGIAN]: {
    [PollyEngine.NEURAL]: { female: ['Ida'], male: [] },
    [PollyEngine.STANDARD]: { female: ['Liv'], male: [] },
  },
  [PollyLanguage.POLISH]: {
    [PollyEngine.NEURAL]: { female: ['Ola'], male: [] },
    [PollyEngine.STANDARD]: { female: ['Ewa', 'Maja'], male: ['Jacek', 'Jan'] },
  },
  [PollyLanguage.PORTUGUESE]: {
    [PollyEngine.NEURAL]: {
      female: ['Camila', 'Vitoria'],
      male: ['Thiago'],
    },
    [PollyEngine.STANDARD]: {
      female: ['Camila', 'Vitoria'],
      male: ['Ricardo'],
    },
    [PollyEngine.GENERATIVE]: { female: ['Camila'], male: [] },
  },
  [PollyLanguage.ROMANIAN]: {
    [PollyEngine.STANDARD]: { female: ['Carmen'], male: [] },
  },
  [PollyLanguage.RUSSIAN]: {
    [PollyEngine.STANDARD]: { female: ['Tatyana'], male: ['Maxim'] },
  },
  [PollyLanguage.SPANISH]: {
    [PollyEngine.NEURAL]: {
      female: ['Lucia', 'Lupe', 'Penelope'],
      male: ['Miguel', 'Sergio'],
    },
    [PollyEngine.STANDARD]: {
      female: ['Conchita', 'Lucia', 'Lupe', 'Mia', 'Penelope'],
      male: ['Enrique', 'Miguel'],
    },
    [PollyEngine.GENERATIVE]: { female: ['Lupe'], male: [] },
  },
  [PollyLanguage.SWEDISH]: {
    [PollyEngine.NEURAL]: { female: ['Elin'], male: [] },
    [PollyEngine.STANDARD]: { female: ['Astrid'], male: [] },
  },
  [PollyLanguage.TURKISH]: {
    [PollyEngine.NEURAL]: { female: ['Burcu'], male: [] },
    [PollyEngine.STANDARD]: { female: ['Filiz'], male: [] },
  },
  [PollyLanguage.WELSH]: {
    [PollyEngine.NEURAL]: { female: ['Gwyneth'], male: [] },
    [PollyEngine.STANDARD]: { female: ['Gwyneth'], male: [] },
  },
};

export const getVoices = (
  language: PollyLanguage,
  engine: PollyEngine,
  gender?: 'female' | 'male',
): string[] => {
  const entry = voicesByLanguage[language]?.[engine];

  if (!entry) {
    return [];
  }

  if (gender === 'female') {
    return entry.female;
  }

  if (gender === 'male') {
    return entry.male;
  }

  return [...entry.female, ...entry.male];
};

export const getAvailableEngines = (
  language: PollyLanguage,
  gender?: 'female' | 'male',
): PollyEngine[] =>
  engines.filter((eng) => getVoices(language, eng, gender).length > 0);

const ENGINE_PRIORITY = [
  PollyEngine.GENERATIVE,
  PollyEngine.NEURAL,
  PollyEngine.STANDARD,
];

export const getDefaultEngine = (
  language: PollyLanguage,
  gender?: 'female' | 'male',
): PollyEngine | undefined =>
  ENGINE_PRIORITY.find((eng) => getVoices(language, eng, gender).length > 0);

export const getDefaultVoice = (
  language: PollyLanguage,
  gender?: 'female' | 'male',
): { engine: PollyEngine; voiceId: string } | undefined => {
  const engine = getDefaultEngine(language, gender);

  if (!engine) {
    return undefined;
  }

  const voices = getVoices(language, engine, gender);

  return { engine, voiceId: voices[0] };
};
